/**
 * The drawings behind the face and body chart: a head seen face on, and a
 * standing figure seen from the front and from behind, each with the zones
 * of `@oxyflows/domain` laid over the anatomy as soft regions.
 *
 * Everything is built from landmark points through one smoothing routine,
 * so the two halves of a face or a body are exact mirror images and a
 * region's edge follows the same curve as the outline beside it.
 *
 * CLINICAL SAFETY — the geometry is written for the patient's RIGHT side as
 * seen from the front, i.e. on the VIEWER'S LEFT. The left side is the
 * reflection; from behind, the reflection swaps again so the patient's right
 * lands on the viewer's right. `zonePath` applies that rule; nothing else
 * may mirror the drawing (the chart sets `direction: ltr` on it).
 */
export type Pt = readonly [number, number];

export const FACE_SIZE = { width: 360, height: 460 } as const;
export const BODY_SIZE = { width: 300, height: 640 } as const;

/**
 * A smooth path through points (Catmull-Rom spline rendered as cubic
 * Béziers). `tension` 1 is the natural spline, 0 is a straight polygon. A
 * point given twice in a row becomes a sharp corner.
 */
export function smoothPath(points: readonly Pt[], { closed = true, tension = 1 } = {}): string {
  const n = points.length;
  const first = points[0];
  if (!first) return '';
  if (n === 1) return `M${first[0]} ${first[1]}`;
  const at = (i: number): Pt => (closed ? points[((i % n) + n) % n] : points[Math.min(Math.max(i, 0), n - 1)]) ?? first;
  const k = tension / 6;
  const f = (v: number) => Math.round(v * 10) / 10;
  let d = `M${f(first[0])} ${f(first[1])}`;
  const segments = closed ? n : n - 1;
  for (let i = 0; i < segments; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) * k;
    const c1y = p1[1] + (p2[1] - p0[1]) * k;
    const c2x = p2[0] - (p3[0] - p1[0]) * k;
    const c2y = p2[1] - (p3[1] - p1[1]) * k;
    d += `C${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return closed ? d + 'Z' : d;
}

const mirror = (points: readonly Pt[], width: number): Pt[] => points.map(([x, y]) => [width - x, y]);
/** A closed outline from its right half, listed top to bottom and ending on the midline. */
const symmetric = (rightHalf: readonly Pt[], width: number): Pt[] => {
  const mid = (p: Pt | undefined) => p !== undefined && Math.abs(p[0] - width / 2) < 0.5;
  let left = mirror(rightHalf, width).reverse();
  if (mid(rightHalf[rightHalf.length - 1])) left = left.slice(1);
  if (mid(rightHalf[0])) left = left.slice(0, -1);
  return [...rightHalf, ...left];
};
const centroid = (points: readonly Pt[]): Pt => {
  const sx = points.reduce((a, p) => a + p[0], 0);
  const sy = points.reduce((a, p) => a + p[1], 0);
  return [Math.round(sx / points.length), Math.round(sy / points.length)];
};

// ─── The face ──────────────────────────────────────────────────────────────
// 360 × 460. Centre line x = 180.

const FW = FACE_SIZE.width;

const HEAD_RIGHT: Pt[] = [
  [180, 26], [160, 28], [136, 40], [112, 60], [92, 100], [78, 152], [76, 204], [86, 258], [100, 306], [124, 348], [154, 374], [180, 380],
];
const HAIRLINE: Pt[] = [[80, 170], [94, 124], [122, 100], [150, 92], [180, 90], [210, 92], [238, 100], [266, 124], [280, 170]];
const BROW_R: Pt[] = [[104, 178], [122, 168], [146, 168], [164, 176]];
const EYE_UPPER_R: Pt[] = [[108, 198], [122, 187], [136, 185], [150, 189], [160, 198]];
const EYE_LOWER_R: Pt[] = [[108, 198], [120, 207], [136, 210], [150, 207], [160, 198]];
const NOSE_SIDE_R: Pt[] = [[172, 196], [168, 228], [162, 250], [164, 264]];
const NOSE_TIP: Pt[] = [[162, 264], [170, 272], [180, 274], [190, 272], [198, 264]];
const NOSTRIL_R: Pt[] = [[160, 256], [152, 262], [154, 270], [164, 268]];
const LIP_UPPER: Pt[] = [[150, 298], [162, 291], [172, 290], [180, 294], [188, 290], [198, 291], [210, 298]];
const LIP_LINE: Pt[] = [[150, 298], [165, 302], [180, 304], [195, 302], [210, 298]];
const LIP_LOWER: Pt[] = [[150, 298], [160, 312], [180, 318], [200, 312], [210, 298]];
const NECK_R: Pt[] = [[142, 370], [136, 400], [128, 436], [124, 460]];
const SHOULDER_R: Pt[] = [[126, 434], [100, 444], [64, 452], [30, 462]];
const EAR_R = 'M78 188C56 182 52 232 74 250M74 200C66 208 66 232 72 242';

export const FACE_DRAWING = {
  head: smoothPath(symmetric(HEAD_RIGHT, FW)),
  ears: [EAR_R, EAR_R.replace(/(-?\d+(?:\.\d+)?) (-?\d+(?:\.\d+)?)/g, (_m, x: string, y: string) => `${FW - Number(x)} ${y}`)],
  hairline: smoothPath(HAIRLINE, { closed: false }),
  brows: [smoothPath(BROW_R, { closed: false }), smoothPath(mirror(BROW_R, FW), { closed: false })],
  eyes: [
    smoothPath(EYE_UPPER_R, { closed: false }), smoothPath(EYE_LOWER_R, { closed: false }),
    smoothPath(mirror(EYE_UPPER_R, FW), { closed: false }), smoothPath(mirror(EYE_LOWER_R, FW), { closed: false }),
  ],
  irises: [{ cx: 134, cy: 197, r: 7.5 }, { cx: FW - 134, cy: 197, r: 7.5 }],
  nose: [
    smoothPath(NOSE_SIDE_R, { closed: false }), smoothPath(NOSE_TIP, { closed: false }),
    smoothPath(NOSTRIL_R, { closed: false }), smoothPath(mirror(NOSTRIL_R, FW), { closed: false }),
  ],
  mouth: [smoothPath(LIP_UPPER, { closed: false }), smoothPath(LIP_LINE, { closed: false }), smoothPath(LIP_LOWER, { closed: false })],
  neck: [smoothPath(NECK_R, { closed: false }), smoothPath(mirror(NECK_R, FW), { closed: false })],
  shoulders: [smoothPath(SHOULDER_R, { closed: false }), smoothPath(mirror(SHOULDER_R, FW), { closed: false })],
};

type Region = { points: Pt[]; hole?: Pt[]; tension?: number };

/** Face zones, the patient's right side and the midline. Keys are zone codes without the `_r` suffix. */
const FACE_REGIONS: Record<string, Region> = {
  scalp: { points: [[84, 162], [92, 98], [126, 48], [180, 32], [234, 48], [268, 98], [276, 162], [262, 124], [232, 102], [180, 92], [128, 102], [98, 124]] },
  forehead: { points: [[94, 172], [102, 130], [136, 106], [180, 100], [224, 106], [258, 130], [266, 172], [236, 168], [180, 162], [124, 168]] },
  glabella: { points: [[180, 156], [200, 178], [180, 200], [160, 178]], tension: 0.8 },
  temple: { points: [[78, 148], [98, 140], [108, 172], [104, 200], [82, 200], [74, 174]] },
  brow: { points: [[100, 186], [106, 172], [128, 162], [152, 162], [168, 172], [166, 184], [146, 176], [122, 178]] },
  periorbital: { points: [[96, 202], [108, 186], [134, 180], [158, 186], [170, 200], [162, 216], [136, 222], [108, 216]] },
  tear_trough: { points: [[112, 214], [136, 212], [158, 214], [160, 226], [136, 232], [110, 228]] },
  nose: { points: [[170, 194], [190, 194], [198, 232], [208, 262], [200, 278], [180, 282], [160, 278], [152, 262], [162, 232]] },
  cheek: { points: [[82, 214], [106, 220], [136, 234], [152, 262], [136, 290], [108, 290], [88, 266], [78, 238]] },
  nasolabial: { points: [[152, 262], [162, 270], [158, 296], [150, 304], [140, 298], [142, 274]] },
  upper_lip: { points: [[148, 298], [160, 288], [180, 284], [200, 288], [212, 298], [198, 306], [180, 308], [162, 306]] },
  lower_lip: { points: [[148, 300], [162, 308], [180, 310], [198, 308], [212, 300], [200, 320], [180, 326], [160, 320]] },
  perioral: {
    points: [[132, 300], [142, 278], [180, 270], [218, 278], [228, 300], [216, 332], [180, 340], [144, 332]],
    hole: [[148, 298], [160, 288], [180, 284], [200, 288], [212, 298], [200, 320], [180, 326], [160, 320]],
  },
  marionette: { points: [[134, 306], [148, 312], [152, 332], [148, 354], [134, 354], [128, 330]] },
  chin: { points: [[150, 336], [180, 334], [210, 336], [216, 354], [202, 376], [180, 382], [158, 376], [144, 354]] },
  jawline: { points: [[92, 272], [104, 292], [118, 328], [140, 354], [160, 368], [148, 380], [126, 368], [104, 344], [88, 316], [82, 292]] },
  masseter: { points: [[80, 242], [98, 248], [106, 274], [108, 300], [98, 314], [84, 302], [76, 272]] },
  neck: { points: [[140, 376], [180, 388], [220, 376], [234, 458], [126, 458]], tension: 0.6 },
};

// ─── The body ──────────────────────────────────────────────────────────────
// 300 × 640. Centre line x = 150. Standing, arms a little away from the
// sides so the underarms and flanks can be reached, feet a little apart.

const BW = BODY_SIZE.width;

const BODY_RIGHT: Pt[] = [
  [136, 84], [133, 104], [112, 110], [86, 122], [70, 148], [63, 192], [57, 236], [49, 290], [43, 340], [39, 372],
  [47, 404], [47, 404], [63, 392], [67, 342], [75, 292], [83, 242], [89, 196], [95, 158], [95, 158], [97, 182],
  [105, 252], [93, 320], [95, 352], [99, 400], [109, 452], [105, 500], [117, 572], [113, 600], [139, 614], [139, 614],
  [143, 596], [141, 572], [139, 500], [139, 452], [143, 400], [150, 358], [150, 358],
];
const COLLARBONE_R: Pt[] = [[150, 118], [130, 116], [110, 112], [92, 120]];
const KNEE_R: Pt[] = [[118, 452], [124, 449], [130, 452]];
const SHOULDER_BLADE_R: Pt[] = [[128, 150], [116, 178], [122, 206]];

export const BODY_DRAWING = {
  body: smoothPath(symmetric(BODY_RIGHT, BW)),
  head: { cx: 150, cy: 50, rx: 27, ry: 33 },
  front: [
    smoothPath(COLLARBONE_R, { closed: false }), smoothPath(mirror(COLLARBONE_R, BW), { closed: false }),
    smoothPath(KNEE_R, { closed: false }), smoothPath(mirror(KNEE_R, BW), { closed: false }),
    'M150 288a2 2 0 1 0 0.1 0', // navel
  ],
  back: [
    'M150 112V330', // spine
    smoothPath(SHOULDER_BLADE_R, { closed: false }), smoothPath(mirror(SHOULDER_BLADE_R, BW), { closed: false }),
    'M150 340C140 356 136 372 138 396M150 340C160 356 164 372 162 396', // gluteal fold
  ],
};

/** Body zones for the front; `BODY_REGIONS_BACK` overrides what differs from behind. */
const BODY_REGIONS: Record<string, Region> = {
  decolletage: { points: [[112, 112], [150, 108], [188, 112], [190, 142], [150, 150], [110, 142]] },
  shoulder: { points: [[88, 122], [110, 113], [116, 128], [102, 150], [84, 152], [74, 138]] },
  underarm: { points: [[86, 152], [100, 152], [104, 180], [94, 196], [82, 186], [80, 166]] },
  chest: { points: [[106, 150], [150, 152], [194, 150], [200, 212], [150, 220], [100, 212]] },
  abdomen: { points: [[108, 214], [150, 222], [192, 214], [194, 322], [150, 338], [106, 322]] },
  flank: { points: [[97, 184], [109, 212], [107, 322], [93, 320], [94, 250], [96, 210]] },
  arm_upper: { points: [[70, 154], [92, 162], [88, 198], [82, 240], [61, 236], [66, 192]] },
  forearm: { points: [[60, 244], [82, 248], [67, 340], [46, 338], [52, 290]] },
  hand: { points: [[44, 344], [66, 344], [62, 390], [48, 400], [42, 372]] },
  bikini: { points: [[108, 326], [150, 340], [192, 326], [178, 364], [150, 384], [122, 364]] },
  thigh: { points: [[95, 352], [122, 358], [148, 360], [143, 402], [139, 442], [109, 442], [99, 400]] },
  knee: { points: [[108, 440], [139, 440], [139, 474], [106, 474]], tension: 0.6 },
  lower_leg: { points: [[106, 474], [139, 474], [141, 572], [117, 572]], tension: 0.6 },
  foot: { points: [[115, 572], [141, 572], [143, 598], [139, 614], [113, 600]], tension: 0.6 },
  back_upper: { points: [[106, 150], [150, 154], [194, 150], [200, 238], [150, 246], [100, 238]] },
  back_lower: { points: [[104, 238], [150, 246], [196, 238], [194, 322], [150, 332], [106, 322]] },
  buttock: { points: [[94, 322], [150, 334], [150, 386], [143, 400], [97, 400], [93, 352]] },
};
const BODY_REGIONS_BACK: Record<string, Region> = {
  thigh: { points: [[97, 400], [122, 402], [143, 400], [139, 442], [109, 442]] },
};

const baseCode = (code: string) => code.replace(/_(r|l)$/, '');
const sideOf = (code: string): 'right' | 'left' | 'midline' => (code.endsWith('_r') ? 'right' : code.endsWith('_l') ? 'left' : 'midline');

function regionFor(code: string, region: 'face' | 'body', view: 'front' | 'back'): Region | null {
  if (region === 'face') return FACE_REGIONS[baseCode(code)] ?? null;
  return (view === 'back' ? BODY_REGIONS_BACK[baseCode(code)] : undefined) ?? BODY_REGIONS[baseCode(code)] ?? null;
}

/** Whether a zone's geometry is reflected in this view (see the file comment). */
export function isMirrored(code: string, view: 'front' | 'back'): boolean {
  const side = sideOf(code);
  if (side === 'midline') return false;
  return (side === 'left') !== (view === 'back');
}

/** The region path of a zone in a view, or null when the zone is not drawn there. */
export function zonePath(code: string, region: 'face' | 'body', view: 'front' | 'back' = 'front'): string | null {
  const r = regionFor(code, region, view);
  if (!r) return null;
  const width = region === 'face' ? FW : BW;
  const flip = isMirrored(code, view);
  const pts = flip ? mirror(r.points, width) : r.points;
  let d = smoothPath(pts, { tension: r.tension ?? 1 });
  if (r.hole) d += smoothPath(flip ? mirror(r.hole, width) : r.hole, { tension: r.tension ?? 1 });
  return d;
}

/** Where a zone's badge sits: the centre of its region. */
export function zoneAnchor(code: string, region: 'face' | 'body', view: 'front' | 'back' = 'front'): Pt | null {
  const r = regionFor(code, region, view);
  if (!r) return null;
  const width = region === 'face' ? FW : BW;
  const c = centroid(r.points);
  return isMirrored(code, view) ? [width - c[0], c[1]] : c;
}
